import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { compressImage, describeUploadError, uploadAvatar } from "@/lib/avatar";
import { PROFILE_UPDATED_EVENT } from "@/components/ProfileCompletionPrompt";
import { EMPTY_MEMBER } from "./leaderboardModel";

export const useTeamLeaderboard = (userId) => {
  const [members, setMembers] = useState([]);
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [deletingMember, setDeletingMember] = useState(null);
  const [newMember, setNewMember] = useState(EMPTY_MEMBER);
  const [photoPreview, setPhotoPreview] = useState(null);

  const fetchTeamMembers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/team/${userId}`);
      setMembers(response.data);
    } catch (error) {
      console.error("Error fetching team members:", error);
    }
  }, [userId]);

  useEffect(() => { fetchTeamMembers(); }, [fetchTeamMembers]);

  useEffect(() => {
    window.addEventListener(PROFILE_UPDATED_EVENT, fetchTeamMembers);
    return () => window.removeEventListener(PROFILE_UPDATED_EVENT, fetchTeamMembers);
  }, [fetchTeamMembers]);

  const handlePhotoUpload = async (e, memberId = null) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const blob = await compressImage(file);
      const { photoUrl } = await uploadAvatar(userId, blob);
      if (memberId) {
        await axios.put(`${API}/team/${memberId}`, { photoUrl });
        toast.success("Photo updated!");
        fetchTeamMembers();
        setEditingMember(null);
        const me = members.find((m) => m.memberId === memberId);
        if (me?.isOwner) window.dispatchEvent(new CustomEvent(PROFILE_UPDATED_EVENT));
      } else {
        setPhotoPreview(photoUrl);
      }
    } catch (error) {
      console.error("Photo upload error:", error);
      toast.error(describeUploadError(error));
    }
  };

  const resetAddForm = () => {
    setIsAddingMember(false);
    setNewMember(EMPTY_MEMBER);
    setPhotoPreview(null);
  };

  const handleAddMember = async () => {
    if (!newMember.name || !newMember.callSign) {
      toast.error("Name and call sign are required");
      return;
    }
    try {
      await axios.post(`${API}/team`, { userId, name: newMember.name, callSign: newMember.callSign, email: newMember.email || null, photoUrl: photoPreview || null });
      toast.success(`${newMember.name} added to the team!`);
      resetAddForm();
      fetchTeamMembers();
    } catch (error) {
      toast.error("Failed to add team member");
    }
  };

  const handleDeleteMember = async (memberId, memberName) => {
    try {
      await axios.delete(`${API}/team/${memberId}`);
      toast.success(`${memberName} removed from team`);
      setDeletingMember(null);
      fetchTeamMembers();
    } catch (error) {
      toast.error("Failed to delete team member");
    }
  };

  return {
    members, isAddingMember, setIsAddingMember, editingMember, setEditingMember, deletingMember, setDeletingMember,
    newMember, setNewMember, photoPreview, setPhotoPreview, handlePhotoUpload, handleAddMember, handleDeleteMember, resetAddForm,
  };
};
